import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loading } from '@/components/loader/loading';
import { exportCampaignFeedbackUrl, fetchCampaignFeedback } from '@/services/feedback';
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/$publicId/_layout/feedback/')({
    component: CampaignFeedbackPage,
});
function CampaignFeedbackPage() {
    const { publicId } = Route.useParams();
    const { t } = useTranslation('campaign');
    const { data, isLoading, isError } = useQuery({
        queryKey: ['campaign-feedback', publicId],
        queryFn: () => fetchCampaignFeedback(publicId),
        staleTime: 30_000,
    });
    if (isLoading) {
        return <Loading loadingText={t('feedback.loading')}/>;
    }
    if (isError || !data) {
        return <p className="p-6 text-sm text-destructive">{t('feedback.load_error')}</p>;
    }
    const total = data.meta.total;
    const submissions = data.data;
    const averageRating = submissions.filter((s) => s.rating !== null).length > 0
        ? submissions.reduce((sum, s) => sum + (s.rating ?? 0), 0) /
            submissions.filter((s) => s.rating !== null).length
        : null;
    return (<div className="flex flex-col gap-6 p-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold">{t('feedback.title')}</h1>
                    <p className="text-sm text-muted-foreground">{t('feedback.subtitle')}</p>
                </div>
                <Button asChild variant="outline" size="sm" disabled={total === 0}>
                    <a href={exportCampaignFeedbackUrl(publicId)} download>
                        <Download className="h-3.5 w-3.5"/>
                        {t('feedback.export_csv')}
                    </a>
                </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <Kpi label={t('feedback.total_submissions')} value={String(total)}/>
                <Kpi label={t('feedback.average_rating')} value={averageRating !== null ? `${averageRating.toFixed(1)} / 5` : '—'}/>
                <Kpi label={t('feedback.delivered_telegram')} value={`${submissions.filter((s) => s.delivered).length} / ${submissions.length}`}/>
            </div>

            {submissions.length === 0 ? (<Card>
                    <CardContent className="py-10 text-center text-sm text-muted-foreground">
                        {t('feedback.empty')}
                    </CardContent>
                </Card>) : (<div className="grid gap-3">
                    {submissions.map((submission) => (<SubmissionCard key={submission.id} submission={submission}/>))}
                </div>)}
        </div>);
}
function Kpi({ label, value }) {
    return (<Card>
            <CardContent className="pt-6">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="text-2xl font-semibold">{value}</p>
            </CardContent>
        </Card>);
}
function SubmissionCard({ submission }) {
    const { t } = useTranslation('campaign');
    const created = new Date(submission.createdAt);
    return (<Card>
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-sm font-normal text-muted-foreground">
                    <span>{created.toLocaleString()}</span>
                    {submission.rating !== null && <RatingStars value={submission.rating}/>}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {submission.comment && (<p className="text-sm leading-relaxed whitespace-pre-wrap">{submission.comment}</p>)}
                {submission.answers && submission.answers.length > 0 && (<dl className="grid gap-1.5 border-t pt-2">
                        {submission.answers.map((answer) => (<AnswerRow key={answer.id} answer={answer}/>))}
                    </dl>)}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {submission.name && <span>{submission.name}</span>}
                    {submission.email && <span>{submission.email}</span>}
                    {submission.entryPoint && (<span>
                            {t('feedback.source', {
                label: submission.entryPoint.name ?? submission.entryPoint.publicId,
            })}
                        </span>)}
                    {!submission.delivered && (<span className="text-amber-500">{t('feedback.not_delivered_telegram')}</span>)}
                </div>
            </CardContent>
        </Card>);
}
function AnswerRow({ answer }) {
    return (<div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <dt className="text-xs font-medium text-muted-foreground sm:w-1/3 sm:shrink-0">{answer.label}</dt>
            <dd className="text-sm">
                {answer.type === 'rating' ? (<RatingStars value={Number(answer.value) || 0}/>) : Array.isArray(answer.value) ? (answer.value.join(', ')) : (<span className="whitespace-pre-wrap">{answer.value}</span>)}
            </dd>
        </div>);
}
function RatingStars({ value }) {
    return (<span className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (<Star key={star} className={'h-3.5 w-3.5 ' + (star <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40')}/>))}
        </span>);
}
